import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaSlidersH } from "react-icons/fa";
import { CrmTask, TaskStatus } from "../../../models/Task";
import { getBoard } from "../../../services/crmService";
import {
  getTasks,
  saveTask,
  updateTask,
} from "../../../services/taskService";
import { getUsers } from "../../../services/userService";
import { crm } from "./crmTheme";

interface UserOption {
  id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}
const userLabel = (u: UserOption) =>
  `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() || u.email || "Usuário";

interface ProspectOption {
  id: string;
  name: string;
  number?: number;
}

const empty: CrmTask = { title: "", description: "" };

// Shared input props
const inputStyle = {
  h: "42px",
  borderColor: crm.border,
  borderRadius: "10px",
  _focus: { borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` },
  fontSize: "sm" as const,
};

const FLabel = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="xs" fontWeight="600" color={crm.textMuted} mb="4px">
    {children}
  </FormLabel>
);

const TaskFormModal = ({
  isOpen,
  onClose,
  onSaved,
  statuses,
  isAdmin,
  taskId,
  defaultProspectId,
  lockProspect,
  onManageStatus,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  statuses: TaskStatus[];
  isAdmin: boolean;
  taskId?: string;
  defaultProspectId?: string;
  lockProspect?: boolean;
  onManageStatus?: () => void;
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [prospects, setProspects] = useState<ProspectOption[]>([]);
  const [form, setForm] = useState<CrmTask>({ ...empty });

  const set = (patch: Partial<CrmTask>) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (isAdmin) getUsers().then((d) => setUsers(d ?? []));
    getBoard().then((cols) => {
      const list: ProspectOption[] = [];
      cols.forEach((c) =>
        c.leads.forEach((l) =>
          list.push({ id: l.id!, name: l.name || "Sem nome", number: l.number })
        )
      );
      setProspects(list);
    });
  }, [isAdmin]);

  useEffect(() => {
    if (!isOpen) return;
    if (taskId) {
      // reusa o GET de tasks (com permissão) para carregar a task em edição
      getTasks().then((all) => {
        const t = all.find((x) => x.id === taskId);
        if (t) setForm({ ...empty, ...t });
      });
    } else {
      setForm({ ...empty, prospectId: defaultProspectId });
    }
  }, [isOpen, taskId, defaultProspectId]);

  const submit = async () => {
    if (!form.title?.trim()) {
      toast({ title: "Informe o título da task.", status: "warning" });
      return;
    }
    setLoading(true);
    try {
      const toIso = (value?: string) => {
        if (!value) return undefined;
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00`;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
        return value;
      };
      const payload: CrmTask = {
        ...form,
        dueDate: toIso(form.dueDate),
        responsibleId: form.responsibleId || undefined,
        taskStatusId: form.taskStatusId || undefined,
        prospectId: form.prospectId || undefined,
      };
      const response = taskId ? await updateTask(payload) : await saveTask(payload);
      if (response && (response.status === 200 || response.data?.success)) {
        toast({
          title: taskId ? "Task atualizada." : "Task criada.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onSaved();
        onClose();
      } else {
        toast({
          title: response?.data?.notifications?.[0] ?? "Erro ao salvar task.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({ title: "Erro ao salvar task.", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Flex justify="space-between" align="center">
            <Text fontWeight="700" fontSize="md" color={crm.text}>
              {taskId ? "Editar task" : "Nova task"}
            </Text>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Box mb="14px">
            <FLabel>Título *</FLabel>
            <Input
              value={form.title ?? ""}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="O que precisa ser feito"
              {...inputStyle}
            />
          </Box>

          <Box mb="14px">
            <FLabel>Descrição</FLabel>
            <Textarea
              value={form.description ?? ""}
              onChange={(e) => set({ description: e.target.value })}
              rows={3}
              borderColor={crm.border}
              borderRadius="10px"
              _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
              fontSize="sm"
            />
          </Box>

          <Flex gap="12px" mb="14px" flexWrap="wrap">
            <Box flex="1" minW="180px">
              <FLabel>Data para conclusão</FLabel>
              <Input
                type="date"
                value={form.dueDate ? form.dueDate.substring(0, 10) : ""}
                onChange={(e) => set({ dueDate: e.target.value })}
                {...inputStyle}
              />
            </Box>
            {isAdmin && (
              <Box flex="1" minW="180px">
                <FLabel>Responsável</FLabel>
                <Select
                  placeholder="Selecione"
                  value={form.responsibleId ?? ""}
                  onChange={(e) => set({ responsibleId: e.target.value })}
                  h="42px"
                  borderColor={crm.border}
                  borderRadius="10px"
                  _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
                  fontSize="sm"
                >
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {userLabel(u)}
                    </option>
                  ))}
                </Select>
              </Box>
            )}
          </Flex>

          <Flex gap="12px" mb="14px" flexWrap="wrap" align="flex-end">
            <Box flex="1" minW="180px">
              <Flex align="center" justify="space-between" mb="4px">
                <FLabel>Status</FLabel>
                {onManageStatus && (
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<FaSlidersH />}
                    color={crm.textMuted}
                    _hover={{ color: crm.brand }}
                    onClick={onManageStatus}
                  >
                    Gerenciar
                  </Button>
                )}
              </Flex>
              <Select
                placeholder="Sem status"
                value={form.taskStatusId ?? ""}
                onChange={(e) => set({ taskStatusId: e.target.value })}
                h="42px"
                borderColor={crm.border}
                borderRadius="10px"
                _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
                fontSize="sm"
              >
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Box>
            <Box flex="1" minW="180px">
              <FLabel>Prospect (opcional)</FLabel>
              <Select
                placeholder="Nenhum"
                value={form.prospectId ?? ""}
                isDisabled={lockProspect}
                onChange={(e) => set({ prospectId: e.target.value })}
                h="42px"
                borderColor={crm.border}
                borderRadius="10px"
                _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
                fontSize="sm"
              >
                {prospects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.number != null ? `#${p.number} ` : ""}
                    {p.name}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`}>
          <Button
            mr="12px"
            variant="ghost"
            color={crm.textMuted}
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            bg={crm.brand}
            color="white"
            _hover={{ bg: crm.brandDark }}
            borderRadius="10px"
            onClick={submit}
            isLoading={loading}
          >
            {taskId ? "Salvar" : "Criar task"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaskFormModal;
